import React, { useCallback} from "react";
import {
  Button,
  MenuItem,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RouteItem from "../config";
import { useNavigate } from "react-router-dom";
import {
  usePopupState,
  bindHover,
  bindMenu
} from 'material-ui-popup-state/hooks';
import HoverMenu from 'material-ui-popup-state/HoverMenu';
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

interface InputProps {
  key: string | number;
  menuItem: RouteItem;
  currentPath: string | null;
  parentPath: string;
  setCurrentPath?: (path: string) => void;
  setParentPath?: (path: string) => void;
  setMobileHamburgerState?: (state: boolean) => void;
}

const SubMenu: React.FC<InputProps> = ({
  menuItem,
  currentPath,
  parentPath,
  setCurrentPath,
  setParentPath,
  setMobileHamburgerState,
}) => {
  const navigate = useNavigate();
  const popupState = usePopupState({ variant: 'popover', popupId: 'submenu' })

  const handleNavigation = useCallback((path: string) => {
    if (setMobileHamburgerState) setMobileHamburgerState(false);
    if (setCurrentPath) setCurrentPath(path);
    if (setParentPath) setParentPath(menuItem.path);
    navigate(`${menuItem.path}/${path}`);
  }, [navigate, setMobileHamburgerState, setCurrentPath, setParentPath, menuItem.path]);

  return (
    <Box sx={{ width: "100%" }}>
      {/* Accordion for mobile view */}
      <Accordion
        sx={{
          backgroundColor: "primary.main",
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          width: "100%",
          minWidth: "100%",
          maxWidth: "100%",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon color="secondary" />}
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{ pr: 6 }}
        >
          {menuItem.title}
        </AccordionSummary>
        <AccordionDetails>
          {menuItem.subRoutes?.map((subMenu) => (
            <Stack
              direction="column"
              alignItems="center"
              key={subMenu.key}
              sx={{ width: "100%" }}
            >
              <Button
                onClick={() => handleNavigation(subMenu.path)}
                sx={{
                  color: currentPath === subMenu.path ? "text.secondary" : "text.primary",
                  bgcolor: currentPath === subMenu.path ? "secondary.main" : "primary.main",
                  ":hover": {
                    bgcolor: "secondary.main",
                    color: "text.secondary",
                  },
                  width: "100%",
                  pr: 8,
                }}
              >
                {subMenu.title}
              </Button>
            </Stack>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* Desktop Menu Button */}

      <Button variant="contained" {...bindHover(popupState)} 
        sx={{ 
          textWrap: "nowrap",
          width: "fit-content",
          // zIndex: (theme) => theme.zIndex.modal + 1,
          color: parentPath === menuItem.path ? "text.secondary" : "text.primary",
          height: "100%",
          px: 1,
          py: 1,
          display: { xs: "none", md: "flex" },
          bgcolor: parentPath === menuItem.path ? "secondary.main" : "primary.main",
          ":hover": {
            bgcolor: "secondary.main",
            color: "text.secondary",
          },
        }}
        endIcon={popupState.isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
      >
        {menuItem.title}
      </Button>

       {/* Dropdown Menu */}
      <HoverMenu 
        {...bindMenu(popupState)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{
          mt: "1px",
          "& .MuiMenu-paper": { backgroundColor: "primary.main" },
        }}
      >
        {menuItem.subRoutes?.map((subMenu) => (
          <MenuItem 
            onClick={() => { handleNavigation(subMenu.path); popupState.close()}}
            sx={{
              color: currentPath === subMenu.path ? "text.secondary" : "text.primary",
              bgcolor: currentPath === subMenu.path ? "secondary.main" : "primary.main",
              ":hover": {
                bgcolor: "secondary.main",
                color: "text.secondary",
              },
            }}
          >
           {subMenu.title}
          </MenuItem>
        ))}

      </HoverMenu>
    </Box>
  );
}

export default React.memo(SubMenu);
